import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Box, CircularProgress } from '@mui/material';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { store } from './store/store';
import { docApi } from './store/docApi';
import { setUser, clearUser, setProfile } from './store/userSlice';
import { useGetUserProfileQuery } from './store/docApi';
import { getTheme } from './theme';
import BottomTabBar from './components/app/BottomTabBar';
import Layout from './components/app/Layout';
import Header from './components/app/Header';
import Login from './User/Login';
import Register from './User/Register';
import RegisterProfile from './User/RegisterProfile';
import WorkspacePage from './Workspace/WorkspacePage';
import WorkspaceEditor from './Workspace/WorkspaceEditor';
import Explore from './Explore/Explore';
import PublicProfile from './Explore/PublicProfile';
import Profile from './User/Profile';
import ProjectList from './Project/ProjectList';
import LibraryItemList from './Library/LibraryItemList';
import LibraryItemDetails from './Library/LibraryItemDetails';
import Welcome from './Welcome';
import Home from './Home';
import ScrollToTop from './components/app/ScrollToTop';
import ProjectCreate from './Project/ProjectCreate';
import Trips from './Trip/Trips';
import TripDetails from './Trip/TripDetails';
import Quests from './Quest/Quests';
import QuestDetails from './Quest/QuestDetails';
import Maker from './Project/Maker';
import Events from './Event/Events';
import EventDetails from './Event/EventDetails';
import Playground from './Chart/Playground';

function LoadingScreen() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default'
      }}
    >
      <CircularProgress size={60} thickness={4} />
    </Box>
  );
}

function AppContent() {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);
  const currentToken = useSelector((state) => state.user.accessToken);
  const profile = useSelector((state) => state.user.profile);
  const [initializing, setInitializing] = useState(true);
  const [mode, setMode] = useState('light'); // Default to light mode

  const { data: profileData, isLoading: profileLoading, error: profileError } = useGetUserProfileQuery(undefined, {
    skip: !userData
  });

  useEffect(() => {
    if (profileData) {
      dispatch(setProfile(profileData));
    }
  }, [profileData, dispatch]);

  function LayoutWithNav() {
    return (
      <>
        <BottomTabBar />
        <Layout.Root>
          <Layout.Header>
            <Header mode={mode} setMode={setMode} />
          </Layout.Header>
          <Layout.Main>
            <Outlet />
            <br />
            <br />
            <br />
          </Layout.Main>
        </Layout.Root>
      </>
    );
  }

  function handleAuthStateChanged(user) {
    if (user) {
      auth.currentUser
        .getIdToken()
        .then(function (idToken) {
          if (idToken === currentToken) {
            setInitializing(false);
            return;
          }
          const _userData = {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            displayName: auth.currentUser.displayName,
            photoURL: auth.currentUser.photoURL
          };
          store.dispatch(setUser({ userData: _userData, accessToken: idToken }));
          dispatch(docApi.util.resetApiState());
          setInitializing(false);
        })
        .catch(function () {
          store.dispatch(clearUser());
          dispatch(docApi.util.resetApiState());
          setInitializing(false);
        });
    } else {
      store.dispatch(clearUser());
      dispatch(docApi.util.resetApiState());
      setInitializing(false);
    }
  }

  useEffect(() => {
    return onAuthStateChanged(auth, handleAuthStateChanged);
  });

  if (initializing || (userData && profileLoading)) {
    return <LoadingScreen />;
  }

  if (!userData) {
    return (
      <ThemeProvider theme={getTheme(mode)}>
        <CssBaseline />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/" element={<Welcome />} />
          <Route path="*" element={<Welcome />} />
        </Routes>
      </ThemeProvider>
    );
  }

  if (userData && !profileLoading && !profileData && (!profile || profileError)) {
    return (
      <ThemeProvider theme={getTheme(mode)}>
        <CssBaseline />
        <Routes>
          <Route path="/register-profile" element={<RegisterProfile />} />
          <Route path="*" element={<Navigate to="/register-profile" replace />} />
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme(mode)}>
      <CssBaseline />
      <Routes>
        {/* Routes with layout */}
        <Route element={<LayoutWithNav />}>
          <Route path="/" element={<Home />} />
          <Route path="/workspaces" element={<WorkspacePage />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/create/:id" element={<ProjectCreate />} />
          <Route path="/library" element={<LibraryItemList />} />
          <Route path="/library/:id" element={<LibraryItemDetails />} />
          <Route path="/library/:label/:pk/:id" element={<LibraryItemDetails />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/explore/:pk" element={<PublicProfile />} />
          <Route path="/explore/:pk/:id" element={<PublicProfile />} />
          <Route path="trips" element={<Trips />} />
          <Route path="trips/:pk/:id" element={<TripDetails />} />
          <Route path="quests" element={<Quests />} />
          <Route path="quests/:questId" element={<QuestDetails />} />
          <Route path="events" element={<Events />} />
          <Route path="events/:pk/:id" element={<EventDetails />} />
          <Route path="knit-chart-playground" element={<Playground />} />
          <Route path="*" element={<Home />} />
        </Route>

        {/* Routes without layout */}
        <Route path="/workspaces/:pk/:id" element={<WorkspaceEditor />} />
        <Route path="/workspaces/:pk/:id/:type" element={<WorkspaceEditor />} />
        <Route path="/workspaces/:pk/:id/:type/:subId" element={<WorkspaceEditor />} />
        <Route path="/workspaces/:pk/:id/:type/:subId/edit" element={<WorkspaceEditor />} />
        <Route path="/projects/:pk/:id" element={<Maker />} />
        <Route path="/projects/:pk/:id/:connectionIndex" element={<Maker />} />
        <Route path="/projects/:pk/:id/:connectionIndex/:type" element={<Maker />} />
        <Route path="/projects/:pk/:id/:connectionIndex/:type/:subId" element={<Maker />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}
